import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// 초기 상태
const initialState = {
  records: [],
  record: null,
  loading: false,
  error: null,
  page: 1,
  pages: 0,
  total: 0,
};

// 사용자 친화적인 오류 메시지 생성 함수
const createUserFriendlyErrorMessage = (error) => {
  let errorMessage = '검색 중 오류가 발생했습니다.';
  
  if (error.code === 'ECONNABORTED' || (error.message && error.message.includes('timeout'))) {
    errorMessage = '서버 응답 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.';
  } else if (error.response) {
    // HTTP 오류 응답
    switch (error.response.status) {
      case 400:
        errorMessage = '잘못된 검색 요청입니다. 검색어를 확인해주세요.';
        break;
      case 401:
        errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
        break;
      case 403:
        errorMessage = '검색 권한이 없습니다.';
        break;
      case 404:
        errorMessage = '검색 서비스를 찾을 수 없습니다.';
        break;
      case 500:
        errorMessage = '서버 내부 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
        break;
      case 502:
      case 503:
      case 504:
        errorMessage = '서버가 일시적으로 응답하지 않습니다. 잠시 후 다시 시도해주세요.';
        break;
      default:
        errorMessage = `오류가 발생했습니다(${error.response.status}). 잠시 후 다시 시도해주세요.`;
    }
    
    // 서버에서 오류 메시지를 제공한 경우 추가
    if (error.response.data && error.response.data.message) {
      errorMessage += ` (${error.response.data.message})`;
    }
  } else if (error.request) {
    // 요청은 보냈으나 응답을 받지 못함
    errorMessage = '서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.';
  }
  
  return errorMessage;
};

// 레코드 검색 액션
export const searchRecords = createAsyncThunk(
  'records/search',
  async ({ query, page = 1, limit = 120 }, { rejectWithValue, getState }) => {
    try {
      // 실제 API 호출 (api 인스턴스가 인증 헤더 자동 첨부)
      const { data } = await api.get(
        `/api/records/search?query=${encodeURIComponent(query)}&page=${page}&limit=${limit}`
      );

      // 검색 기록 저장
      const { auth } = getState();
      if (auth.userInfo) {
        try {
          await api.post('/api/users/search-history', { query });
        } catch (historyError) {
          // 검색 기록 저장 실패는 무시하고 계속 진행
        }
      }

      return data;
    } catch (error) {
      console.error('검색 오류:', error);
      return rejectWithValue(createUserFriendlyErrorMessage(error));
    }
  }
);

// 레코드 상세 조회 액션
export const getRecordDetails = createAsyncThunk(
  'records/details',
  async (id, { rejectWithValue }) => {
    try {
      // 실제 API 호출 (api 인스턴스가 인증 헤더 자동 첨부)
      const { data } = await api.get(`/api/records/${id}`);
      
      return data;
    } catch (error) {
      console.error('레코드 상세 조회 오류:', error);
      return rejectWithValue(createUserFriendlyErrorMessage(error));
    }
  }
);

// 레코드 슬라이스
const recordSlice = createSlice({
  name: 'records',
  initialState,
  reducers: {
    clearRecords: (state) => {
      state.records = [];
      state.page = 1;
      state.pages = 0;
      state.total = 0;
    },
    clearRecordDetails: (state) => {
      state.record = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 레코드 검색
      .addCase(searchRecords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchRecords.fulfilled, (state, action) => {
        state.loading = false;
        state.records = action.payload.records;
        state.page = action.payload.page;
        state.pages = action.payload.pages;
        state.total = action.payload.total;
      })
      .addCase(searchRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 레코드 상세 조회
      .addCase(getRecordDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRecordDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.record = action.payload;
      })
      .addCase(getRecordDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearRecords, clearRecordDetails, clearError } = recordSlice.actions;

export default recordSlice.reducer;